import { useState, useEffect } from 'react';
import { cachedFetchJSON, GEO_TTL } from '../utils/fetchCache';

const CENSUS_BASE = 'https://api.census.gov/data';
const API_KEY = process.env.REACT_APP_CENSUS_API_KEY;

/**
 * Fetches the list of counties in the configured state.
 * Excludes the home county so users can only pick a comparison county.
 *
 * Returns:
 *   counties: [{ fips: '001', name: 'Adams County' }] sorted alphabetically
 *   loading: boolean
 *   error: string | null
 */
export function useCountyList(config) {
  const [counties, setCounties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const state = config.org.geography.state;
  const homeCounty = config.org.geography.county;
  const latestYear = Math.max(...config.indicators.flatMap(i => i.years ?? []));

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        get: 'NAME',
        for: 'county:*',
        in: `state:${state}`,
        ...(API_KEY ? { key: API_KEY } : {})
      });

      try {
        const rows = await cachedFetchJSON(`${CENSUS_BASE}/${latestYear}/acs/acs5?${params}`, GEO_TTL);
        // rows[0] is headers: ['NAME', 'state', 'county']
        const list = rows.slice(1)
          .filter(row => row[2] !== homeCounty)
          .map(row => ({
            fips: row[2],
            // Strip ", State Name" suffix to keep the label short
            name: row[0].split(',')[0].trim()
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        if (!cancelled) {
          setCounties(list);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(`Could not load county list: ${err.message}`);
          setLoading(false);
        }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [state, homeCounty, latestYear]);

  return { counties, loading, error };
}
